'use client'

import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useInterval } from 'usehooks-ts'

import { useServerTime } from '@/service/chart.hook'

export default function Timer() {
  const { data: serverTime, dataUpdatedAt } = useServerTime()
  const [currentTime, setCurrentTime] = useState<Date>(new Date())

  // Store the latest offset between server time and the browser clock.
  const offsetRef = useRef<number | null>(null)

  // Recalibrate after every server-time refetch, including window-focus refreshes.
  useEffect(() => {
    const serverTimestamp = Number(serverTime?.serverTime)
    if (!Number.isFinite(serverTimestamp) || serverTimestamp <= 0 || dataUpdatedAt <= 0) return

    // The query may already contain an older cached timestamp when this Timer mounts.
    // Compare it with the moment React Query actually received it, not with mount time.
    offsetRef.current = serverTimestamp - dataUpdatedAt
  }, [dataUpdatedAt, serverTime?.serverTime])

  // Update time every second using local clock + offset
  useInterval(() => {
    if (offsetRef.current !== null) {
      setCurrentTime(new Date(Date.now() + offsetRef.current))
    } else {
      // Fallback to local time if offset not yet calculated
      setCurrentTime(new Date())
    }
  }, 1000)

  return (
    <time
      className="text-foreground/50 w-27 font-sans text-[11px] leading-none tracking-tight whitespace-pre"
      dateTime={currentTime.toISOString()}
    >
      {format(currentTime, 'HH:mm:ss')} (UTC+{Math.abs(currentTime.getTimezoneOffset() / 60)})
    </time>
  )
}
